import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import Pagination from "src/components/Pagination/Pagination";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import useAdminHashtagsPaged from "../../hooks/useAdminHashtagsPaged/useAdminHashtagsPaged";
import useDeleteHashtag from "../../hooks/useDeleteHashtag";
import { mapHashtagsToContentTableItems } from "../../utils/mapHashtagsToContentTableItems";
import BlockedWordsSection from "../../components/BlockedWordsSection/BlockedWordsSection";

const hashtagColumns: ContentTableColumn[] = [
	{
		key: "color",
		label: "Colour",
		width: "80px",
		render: (item) => {
			const color = (item.data?.color as string) || "#ccc";
			return (
				<span
					className="inline-block w-5 h-5 rounded-full border border-border"
					style={{ backgroundColor: color }}
					title={color}
				>
					<span className="sr-only">{color}</span>
				</span>
			);
		}
	},
	{
		key: "isPrivate",
		label: "Private",
		width: "90px",
		render: (item) => (item.data?.isPrivate ? "Yes" : "No")
	},
	{
		key: "isFavourite",
		label: "Favourite",
		width: "100px",
		render: (item) => (item.data?.isFavourite ? "Yes" : "No")
	},
	{
		key: "source",
		label: "Source",
		width: "140px",
		render: (item) =>
			item.data?.source === "user_generated" ? "User Generated" : "Admin"
	},
	{
		key: "createdBy",
		label: "Created by",
		width: "200px",
		render: (item) => item.createdBy ?? "—"
	}
];

const Hashtags = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);
	const [page, setPage] = useState(1);
	const [sourceFilter, setSourceFilter] = useState("");

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	useEffect(() => {
		setPage(1);
	}, [sourceFilter]);

	const { data, isLoading, isFetching } = useAdminHashtagsPaged(
		debouncedSearch,
		page,
		DEFAULT_ADMIN_PAGE_SIZE,
		sourceFilter
	);
	const { mutate: deleteHashtag, isLoading: isDeleting } = useDeleteHashtag();

	const totalCount = data?.count ?? 0;
	const items = mapHashtagsToContentTableItems(data?.results ?? []);

	return (
		<Page>
			<ContentTable
				items={items}
				columns={hashtagColumns}
				minWidth="900px"
				showCreatedBy={false}
				showLastUpdated={false}
				showOrder={false}
				showStatus={false}
				searchValue={searchInput}
				onSearchChange={setSearchInput}
				searchPlaceholder="Search hashtags..."
				isLoading={isLoading || isFetching}
				onRowClick={(item) => navigate(`/social-feed/hashtags/${item.id}`)}
				onDelete={(item) => setItemToDelete(item)}
				createPath="/social-feed/hashtags/create"
				createLabel="Create Hashtag"
				filterSlot={
					<select
						value={sourceFilter}
						onChange={(e) => setSourceFilter(e.target.value)}
						aria-label="Filter by source"
						className="rounded-lg border border-border bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
					>
						<option value="">All Sources</option>
						<option value="admin">Admin</option>
						<option value="user_generated">User Generated</option>
					</select>
				}
			/>

			<Pagination
				page={page}
				pageSize={DEFAULT_ADMIN_PAGE_SIZE}
				totalCount={totalCount}
				onPageChange={setPage}
				itemLabel="hashtags"
			/>

			<BlockedWordsSection />

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete hashtag?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteHashtag(String(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Hashtags;
